import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import apiService, { GymRegistrationData } from '../services/api';

const GymRegistrationPage: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  usePageTitle('Register New Gym');
  
  const [formData, setFormData] = useState<GymRegistrationData>({
    gym: {
      name: '',
      phone: '',
      email: '',
      logo_url: '',
      address: '',
      description: '',
    },
    owner: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      password: '',
    },
    setup: {
      workout_types: ['strength', 'cardio'],
      class_types: ['group', 'personal'],
    },
  });

  const workoutTypeOptions = [
    { value: 'strength', label: 'Strength Training' },
    { value: 'cardio', label: 'Cardio' },
    { value: 'flexibility', label: 'Flexibility' },
    { value: 'functional', label: 'Functional Fitness' },
    { value: 'yoga', label: 'Yoga' },
    { value: 'pilates', label: 'Pilates' },
  ];

  const classTypeOptions = [
    { value: 'group', label: 'Group Classes' },
    { value: 'personal', label: 'Personal Training' },
    { value: 'specialty', label: 'Specialty Classes' },
    { value: 'bootcamp', label: 'Bootcamp' },
  ];

  const handleInputChange = (section: 'gym' | 'owner', field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleSetupChange = (type: 'workout_types' | 'class_types', value: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      setup: {
        ...prev.setup,
        [type]: checked
          ? [...prev.setup[type], value]
          : prev.setup[type].filter(item => item !== value),
      },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage(null);

    try {
      await apiService.registerGym(formData);
      setMessage({
        type: 'success',
        text: 'Gym registered successfully! You can now log in with your owner account.',
      });
      
      // Redirect to login after a short delay
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || error.message || 'Registration failed',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Register New Gym</h1>
        <p className="mt-1 text-sm text-gray-500">
          Create a new gym account and get started with Central Core+.
        </p>
      </div>

      {message && (
        <div className={`rounded-md p-4 ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800' 
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Gym Information */}
        <div className="card p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Gym Information</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">Gym Name *</label>
              <input
                type="text"
                required
                value={formData.gym.name}
                onChange={(e) => handleInputChange('gym', 'name', e.target.value)}
                className="mt-1 input-field"
                placeholder="Enter gym name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Phone *</label>
              <input
                type="tel"
                required
                value={formData.gym.phone}
                onChange={(e) => handleInputChange('gym', 'phone', e.target.value)}
                className="mt-1 input-field"
                placeholder="Enter phone number"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Email *</label>
              <input
                type="email"
                required
                value={formData.gym.email}
                onChange={(e) => handleInputChange('gym', 'email', e.target.value)}
                className="mt-1 input-field"
                placeholder="Enter gym email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Logo URL</label>
              <input
                type="url"
                value={formData.gym.logo_url}
                onChange={(e) => handleInputChange('gym', 'logo_url', e.target.value)}
                className="mt-1 input-field"
                placeholder="Enter logo URL (optional)"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Address *</label>
              <input
                type="text"
                required
                value={formData.gym.address}
                onChange={(e) => handleInputChange('gym', 'address', e.target.value)}
                className="mt-1 input-field"
                placeholder="Enter gym address"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                value={formData.gym.description}
                onChange={(e) => handleInputChange('gym', 'description', e.target.value)}
                rows={3}
                className="mt-1 input-field"
                placeholder="Enter gym description (optional)"
              />
            </div>
          </div>
        </div>

        {/* Owner Information */}
        <div className="card p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Owner Information</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">First Name *</label>
              <input
                type="text"
                required
                value={formData.owner.first_name}
                onChange={(e) => handleInputChange('owner', 'first_name', e.target.value)}
                className="mt-1 input-field"
                placeholder="Enter first name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Last Name *</label>
              <input
                type="text"
                required
                value={formData.owner.last_name}
                onChange={(e) => handleInputChange('owner', 'last_name', e.target.value)}
                className="mt-1 input-field"
                placeholder="Enter last name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Email *</label>
              <input
                type="email"
                required
                value={formData.owner.email}
                onChange={(e) => handleInputChange('owner', 'email', e.target.value)}
                className="mt-1 input-field"
                placeholder="Enter owner email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Phone</label>
              <input
                type="tel"
                value={formData.owner.phone}
                onChange={(e) => handleInputChange('owner', 'phone', e.target.value)}
                className="mt-1 input-field"
                placeholder="Enter phone number (optional)"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Password *</label>
              <input
                type="password"
                required
                minLength={6}
                value={formData.owner.password}
                onChange={(e) => handleInputChange('owner', 'password', e.target.value)}
                className="mt-1 input-field"
                placeholder="Enter password (min 6 characters)"
              />
            </div>
          </div>
        </div>

        {/* Setup Options */}
        <div className="card p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Initial Setup</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Workout Types</h3>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {workoutTypeOptions.map((option) => (
                  <label key={option.value} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.setup.workout_types.includes(option.value)}
                      onChange={(e) => handleSetupChange('workout_types', option.value, e.target.checked)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Class Types</h3>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {classTypeOptions.map((option) => (
                  <label key={option.value} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.setup.class_types.includes(option.value)}
                      onChange={(e) => handleSetupChange('class_types', option.value, e.target.checked)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary px-8 py-3 text-base disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Registering...
              </div>
            ) : (
              'Register Gym'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default GymRegistrationPage; 